import { useEffect, useState } from "react";
import { createRoute } from "@tanstack/react-router";
import { Route as AdminLayoutRoute } from "./_layout";
import { Badge, BadgeWithDot } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { TextArea } from "@/components/base/textarea/textarea";
import { InlineAlert } from "@/components/application/alerts/inline-alert";
import { Card, CardBody, CardHeader } from "@/components/application/card/card";
import { Modal } from "@/components/application/modals/modal-controlled";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
// useToast nemá zatím nativní UUI ekvivalent — ponecháno na legacy barrelu (řeší samostatný teardown).
import { useToast } from "@/components/application/notifications/toast-provider";
import { FioApiUnavailableBanner } from "../../components/custom/FioApiUnavailableBanner";
import { FioTransactionsSlideOver } from "../../components/custom/FioTransactionsSlideOver";
import {
  useCreatePaymentAccount,
  usePaymentAccountList,
  useRotatePaymentAccountToken,
  useSyncPaymentAccount,
  useUpdatePaymentAccount,
} from "../../hooks/usePaymentAccountQueries";
import {
  PAYMENT_ACCOUNT_MODE_OPTIONS,
  type PaymentAccountMode,
  type PaymentAccountRow,
} from "../../schemas/paymentAccount";
import { ApiError } from "../../lib/apiClient";

export const Route = createRoute({
  getParentRoute: () => AdminLayoutRoute,
  path: "/admin/payment-accounts",
  component: PaymentAccountsPage,
});

/** FIO povoluje 1 dotaz / 30 s / token (jinak vrací 409) — po syncu držíme cooldown. */
const FIO_SYNC_COOLDOWN_MS = 30_000;

function PaymentAccountsPage() {
  const query = usePaymentAccountList();
  const [createOpen, setCreateOpen] = useState(false);
  const [rotateTarget, setRotateTarget] = useState<PaymentAccountRow | null>(null);
  const [editTarget, setEditTarget] = useState<PaymentAccountRow | null>(null);
  const [txTarget, setTxTarget] = useState<PaymentAccountRow | null>(null);

  // Sdílený sync zámek: během běžícího syncu jsou VŠECHNA sync tlačítka neaktivní
  // (nelze omylem spustit druhý sync), + per-účet 30s cooldown po doběhnutí
  // (FIO limit 1 dotaz/30s/token → jinak 409). Odpočet se zobrazuje na tlačítku.
  const [activeSyncId, setActiveSyncId] = useState<string | null>(null);
  const [cooldownUntil, setCooldownUntil] = useState<Record<string, number>>({});
  const [now, setNow] = useState(() => Date.now());

  const hasActiveCooldown = Object.values(cooldownUntil).some((t) => t > now);
  useEffect(() => {
    if (activeSyncId === null && !hasActiveCooldown) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [activeSyncId, hasActiveCooldown]);

  const handleSyncStart = (accountId: string) => setActiveSyncId(accountId);
  const handleSyncSettled = (accountId: string) => {
    setActiveSyncId(null);
    setCooldownUntil((prev) => ({ ...prev, [accountId]: Date.now() + FIO_SYNC_COOLDOWN_MS }));
  };

  return (
    <section aria-labelledby="payment-accounts-title" className="max-w-7xl mx-auto space-y-6">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex flex-col gap-1">
          <h2 id="payment-accounts-title" className="text-display-xs font-bold text-primary m-0">
            FIO platební účty
          </h2>
          <p className="text-md text-tertiary m-0">
            Více účtů FIO — osobní (S1 P2P) a firemní (S0/S2 B2B).
          </p>
        </div>
        <Button
          color="primary"
          onPress={() => setCreateOpen(true)}
          data-testid="payment-account-add"
        >
          Přidat účet
        </Button>
      </header>

      {query.isLoading && <LoadingIndicator label="Načítání…" />}

      {query.isError && (
        <InlineAlert variant="error" title="Chyba načítání">
          Nelze načíst seznam platebních účtů.
        </InlineAlert>
      )}

      {!query.isLoading && !query.isError && (
        <>
          {(query.data?.accounts ?? []).length === 0 ? (
            <InlineAlert variant="info" title="Žádné účty">
              Zatím není nakonfigurován žádný FIO platební účet. Začni tlačítkem
              „Přidat účet“ vpravo nahoře.
            </InlineAlert>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="payment-accounts-list">
              {(query.data?.accounts ?? []).map((account) => (
                <PaymentAccountCard
                  key={account.account_id}
                  account={account}
                  anySyncing={activeSyncId !== null}
                  cooldownRemainingS={Math.max(
                    0,
                    Math.ceil(((cooldownUntil[account.account_id] ?? 0) - now) / 1000),
                  )}
                  onSyncStart={handleSyncStart}
                  onSyncSettled={handleSyncSettled}
                  onRotateToken={() => setRotateTarget(account)}
                  onEdit={() => setEditTarget(account)}
                  onViewTransactions={() => setTxTarget(account)}
                />
              ))}
            </div>
          )}
        </>
      )}

      {createOpen && (
        <CreatePaymentAccountModal onClose={() => setCreateOpen(false)} />
      )}

      {rotateTarget !== null && (
        <RotateTokenModal
          account={rotateTarget}
          onClose={() => setRotateTarget(null)}
        />
      )}

      {editTarget !== null && (
        <EditPaymentAccountModal
          account={editTarget}
          onClose={() => setEditTarget(null)}
        />
      )}

      {txTarget !== null && (
        <FioTransactionsSlideOver
          account={txTarget}
          onClose={() => setTxTarget(null)}
        />
      )}
    </section>
  );
}

interface PaymentAccountCardProps {
  account: PaymentAccountRow;
  /** true = právě běží sync (libovolného účtu) → tlačítko neaktivní. */
  anySyncing: boolean;
  /** Zbývající sekundy FIO cooldownu pro TENTO účet (0 = bez cooldownu). */
  cooldownRemainingS: number;
  onSyncStart: (accountId: string) => void;
  onSyncSettled: (accountId: string) => void;
  onRotateToken: () => void;
  onEdit: () => void;
  onViewTransactions: () => void;
}

function PaymentAccountCard({
  account,
  anySyncing,
  cooldownRemainingS,
  onSyncStart,
  onSyncSettled,
  onRotateToken,
  onEdit,
  onViewTransactions,
}: PaymentAccountCardProps) {
  const sync = useSyncPaymentAccount(account.account_id);
  const toast = useToast();

  function triggerSync() {
    onSyncStart(account.account_id);
    sync.mutate(undefined, {
      onSuccess: (data) => {
        toast.push({
          variant: "success",
          message: `Sync ${account.label}: fetched=${data.fetched}, ingested=${data.ingested}, paired=${data.paired}.`,
        });
      },
      onError: (err) => {
        const message =
          err instanceof ApiError
            ? errorCodeToMessage(err.errorCode) ?? err.message
            : "Sync selhal.";
        toast.push({ variant: "error", message });
      },
      onSettled: () => onSyncSettled(account.account_id),
    });
  }

  const cooling = cooldownRemainingS > 0;

  return (
    <Card data-testid={`payment-account-${account.account_id}`}>
      <CardHeader>
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            <strong className="text-primary">{account.label}</strong>
            <BadgeWithDot color={account.deactivated ? "warning" : "success"}>
              {account.deactivated ? "Deaktivován" : "Aktivní"}
            </BadgeWithDot>
            <Badge color="blue">{labelForMode(account.mode)}</Badge>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              size="sm"
              color="secondary"
              onPress={triggerSync}
              isLoading={sync.isPending}
              isDisabled={account.deactivated || anySyncing || cooling}
              data-testid={`payment-account-sync-${account.account_id}`}
            >
              {cooling ? `Sync za ${cooldownRemainingS} s` : "Sync teď"}
            </Button>
            <Button
              size="sm"
              color="secondary"
              onPress={onViewTransactions}
              data-testid={`payment-account-transactions-${account.account_id}`}
            >
              Zobrazit stažené transakce
            </Button>
            <Button
              size="sm"
              color="secondary"
              onPress={onEdit}
              data-testid={`payment-account-edit-${account.account_id}`}
            >
              Upravit
            </Button>
            <Button
              size="sm"
              color="secondary"
              onPress={onRotateToken}
              data-testid={`payment-account-rotate-${account.account_id}`}
            >
              Rotovat token
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardBody>
        <FioApiUnavailableBanner
          accountLabel={account.label}
          lastSyncedAt={account.last_synced_at}
        />
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 m-0 mt-3">
          <dt className="text-sm text-tertiary">Účet</dt>
          <dd className="text-sm text-primary m-0">
            <code className="font-mono">{account.fio_account_number}</code>
          </dd>
          <dt className="text-sm text-tertiary">Token</dt>
          <dd className="text-sm text-primary m-0">
            {account.has_token ? "✓ nastaven" : "— chybí"}
          </dd>
          <dt className="text-sm text-tertiary">Poslední sync</dt>
          <dd
            className="text-sm text-primary m-0"
            data-testid={`payment-account-last-sync-${account.account_id}`}
          >
            {formatDateTime(account.last_synced_at)}
          </dd>
        </dl>
      </CardBody>
    </Card>
  );
}

interface CreateModalProps {
  onClose: () => void;
}

function CreatePaymentAccountModal({ onClose }: CreateModalProps) {
  const create = useCreatePaymentAccount();
  const toast = useToast();
  const [accountNumber, setAccountNumber] = useState("");
  const [mode, setMode] = useState<PaymentAccountMode>("personal");
  const [label, setLabel] = useState("");
  const [token, setToken] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  function submit() {
    const trimmedNumber = accountNumber.trim();
    const trimmedLabel = label.trim();
    if (trimmedNumber.length === 0) {
      setFormError("Číslo účtu je povinné.");
      return;
    }
    if (trimmedLabel.length === 0) {
      setFormError("Popisek účtu je povinný.");
      return;
    }
    setFormError(null);
    create.mutate(
      {
        fio_account_number: trimmedNumber,
        mode,
        label: trimmedLabel,
        fio_api_token: token.trim().length > 0 ? token.trim() : null,
      },
      {
        onSuccess: () => {
          toast.push({
            variant: "success",
            message: `Účet „${trimmedLabel}“ vytvořen.`,
          });
          onClose();
        },
        onError: (err) => {
          setFormError(
            err instanceof ApiError
              ? errorCodeToMessage(err.errorCode) ?? err.message
              : "Vytvoření selhalo.",
          );
        },
      },
    );
  }

  const submitting = create.isPending;
  const modeHint = PAYMENT_ACCOUNT_MODE_OPTIONS.find((m) => m.value === mode)?.hint;

  return (
    <Modal
      open
      title="Přidat FIO platební účet"
      onClose={submitting ? () => {} : onClose}
      footer={
        <>
          <Button color="secondary" onPress={onClose} isDisabled={submitting}>
            Zrušit
          </Button>
          <Button
            color="primary"
            onPress={submit}
            isDisabled={submitting}
            isLoading={submitting}
            data-testid="payment-account-create-submit"
          >
            Vytvořit
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {formError && (
          <InlineAlert variant="error" title="Chyba">
            {formError}
          </InlineAlert>
        )}
        <Input
          label="Číslo FIO účtu"
          isRequired
          hint="Formát např. 2901234567/2010"
          value={accountNumber}
          onChange={setAccountNumber}
          placeholder="2901234567/2010"
          isDisabled={submitting}
          data-testid="payment-account-create-number"
        />
        <Input
          label="Popisek"
          isRequired
          hint="Krátký název účtu pro orientaci v UI."
          value={label}
          onChange={setLabel}
          placeholder="Např. Petr osobní"
          isDisabled={submitting}
          data-testid="payment-account-create-label"
        />
        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-secondary">
            Typ účtu<span aria-hidden="true"> *</span>
          </span>
          <select
            className="block w-full min-h-touch rounded-lg bg-primary px-3 py-2 text-md text-primary ring-1 ring-inset ring-primary outline-hidden focus:ring-2 focus:ring-brand disabled:opacity-50"
            value={mode}
            onChange={(e) => setMode(e.target.value as PaymentAccountMode)}
            disabled={submitting}
            data-testid="payment-account-create-mode"
          >
            {PAYMENT_ACCOUNT_MODE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {modeHint && <span className="text-sm text-tertiary">{modeHint}</span>}
        </label>
        <TextArea
          label="FIO API token (volitelně)"
          hint="Token zašifrujeme a uložíme. Bez tokenu se účet vytvoří, ale sync nepoběží — token lze doplnit přes „Rotovat token“."
          rows={3}
          value={token}
          onChange={setToken}
          placeholder="64znakový hex token z FIO Internetbanking…"
          isDisabled={submitting}
          data-testid="payment-account-create-token"
        />
      </div>
    </Modal>
  );
}

interface EditModalProps {
  account: PaymentAccountRow;
  onClose: () => void;
}

function EditPaymentAccountModal({ account, onClose }: EditModalProps) {
  const update = useUpdatePaymentAccount(account.account_id);
  const toast = useToast();
  const [label, setLabel] = useState(account.label);
  const [mode, setMode] = useState<PaymentAccountMode>(
    PAYMENT_ACCOUNT_MODE_OPTIONS.some((m) => m.value === account.mode)
      ? (account.mode as PaymentAccountMode)
      : "personal",
  );
  const [token, setToken] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  function submit() {
    const trimmedLabel = label.trim();
    if (trimmedLabel.length === 0) {
      setFormError("Popisek účtu je povinný.");
      return;
    }
    setFormError(null);
    update.mutate(
      {
        label: trimmedLabel,
        mode,
        fio_api_token: token.trim().length > 0 ? token.trim() : null,
      },
      {
        onSuccess: () => {
          toast.push({
            variant: "success",
            message: `Účet „${trimmedLabel}“ uložen.`,
          });
          onClose();
        },
        onError: (err) => {
          setFormError(
            err instanceof ApiError
              ? errorCodeToMessage(err.errorCode) ?? err.message
              : "Uložení selhalo.",
          );
        },
      },
    );
  }

  const submitting = update.isPending;
  const modeHint = PAYMENT_ACCOUNT_MODE_OPTIONS.find((m) => m.value === mode)?.hint;

  return (
    <Modal
      open
      title={`Upravit účet — ${account.label}`}
      onClose={submitting ? () => {} : onClose}
      footer={
        <>
          <Button color="secondary" onPress={onClose} isDisabled={submitting}>
            Zrušit
          </Button>
          <Button
            color="primary"
            onPress={submit}
            isDisabled={submitting}
            isLoading={submitting}
            data-testid="payment-account-edit-submit"
          >
            Uložit
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {formError && (
          <InlineAlert variant="error" title="Chyba">
            {formError}
          </InlineAlert>
        )}
        <Input
          label="Číslo FIO účtu"
          hint="Číslo účtu nelze měnit (je vázané na šifrování tokenu)."
          value={account.fio_account_number}
          isDisabled
          isReadOnly
        />
        <Input
          label="Popisek"
          isRequired
          hint="Krátký název účtu pro orientaci v UI."
          value={label}
          onChange={setLabel}
          isDisabled={submitting}
          data-testid="payment-account-edit-label"
        />
        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-secondary">
            Typ účtu<span aria-hidden="true"> *</span>
          </span>
          <select
            className="block w-full min-h-touch rounded-lg bg-primary px-3 py-2 text-md text-primary ring-1 ring-inset ring-primary outline-hidden focus:ring-2 focus:ring-brand disabled:opacity-50"
            value={mode}
            onChange={(e) => setMode(e.target.value as PaymentAccountMode)}
            disabled={submitting}
            data-testid="payment-account-edit-mode"
          >
            {PAYMENT_ACCOUNT_MODE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {modeHint && <span className="text-sm text-tertiary">{modeHint}</span>}
        </label>
        <TextArea
          label="FIO API token (volitelně)"
          hint="Vyplň jen pokud chceš token změnit. Prázdné = ponechat stávající token."
          rows={3}
          value={token}
          onChange={setToken}
          placeholder={account.has_token ? "Token je nastaven — vyplň jen pro změnu" : "Token zatím chybí — vlož pro nastavení"}
          isDisabled={submitting}
          data-testid="payment-account-edit-token"
        />
      </div>
    </Modal>
  );
}

interface RotateModalProps {
  account: PaymentAccountRow;
  onClose: () => void;
}

function RotateTokenModal({ account, onClose }: RotateModalProps) {
  const rotate = useRotatePaymentAccountToken(account.account_id);
  const toast = useToast();
  const [token, setToken] = useState("");
  const [error, setError] = useState<string | null>(null);

  function submit() {
    const trimmed = token.trim();
    if (trimmed.length === 0) {
      setError("Token nesmí být prázdný.");
      return;
    }
    setError(null);
    rotate.mutate(
      { fio_api_token: trimmed },
      {
        onSuccess: (data) => {
          toast.push({
            variant: "success",
            message: `Token účtu „${account.label}“ rotován (nový kid ${data.new_kid.slice(0, 8)}…).`,
          });
          onClose();
        },
        onError: (err) => {
          setError(
            err instanceof ApiError
              ? errorCodeToMessage(err.errorCode) ?? err.message
              : "Rotace selhala.",
          );
        },
      },
    );
  }

  const submitting = rotate.isPending;

  return (
    <Modal
      open
      title={`Rotovat token — ${account.label}`}
      onClose={submitting ? () => {} : onClose}
      footer={
        <>
          <Button color="secondary" onPress={onClose} isDisabled={submitting}>
            Zrušit
          </Button>
          <Button
            color="primary"
            onPress={submit}
            isDisabled={submitting}
            isLoading={submitting}
            data-testid="payment-account-rotate-submit"
          >
            Rotovat
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && (
          <InlineAlert variant="error" title="Chyba">
            {error}
          </InlineAlert>
        )}
        <p className="text-sm text-tertiary m-0">
          Vlož nový FIO API token. Předchozí token bude přepsán a další sync půjde
          s novým klíčem.
        </p>
        <TextArea
          label="Nový FIO API token"
          isRequired
          rows={3}
          value={token}
          onChange={setToken}
          placeholder="64znakový hex token z FIO Internetbanking…"
          isDisabled={submitting}
          data-testid="payment-account-rotate-token"
        />
      </div>
    </Modal>
  );
}

function labelForMode(mode: string): string {
  const opt = PAYMENT_ACCOUNT_MODE_OPTIONS.find((m) => m.value === mode);
  return opt?.label ?? mode;
}

function formatDateTime(iso: string | null): string {
  if (!iso) return "nikdy";
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

function errorCodeToMessage(code: string | undefined): string | null {
  switch (code) {
    case "PAYMENT_ACCOUNT_INVALID_PAYLOAD":
      return "Neplatné údaje účtu — zkontroluj formát.";
    case "PAYMENT_ACCOUNT_NOT_FOUND":
      return "Účet neexistuje (mohl být smazán jiným adminem).";
    case "FIO_NO_TOKEN_CONFIGURED":
      return "Účet nemá nastavený FIO token. Nejdřív ho doplň přes „Rotovat token“.";
    case "FIO_API_UNREACHABLE":
      return "FIO API nedostupné. Sync zkus znovu později.";
    case "FIO_RATE_LIMITED":
      return "FIO povoluje jen 1 dotaz za 30 sekund na token. Počkej chvíli a zkus sync znovu.";
    case "FIO_TOKEN_DECRYPT_FAILED":
      return "Uložený token nelze dešifrovat. Rotuj token znovu.";
    case "FIO_PAIRING_INTERNAL_ERROR":
      return "Pairing selhal vnitřní chybou. Zkontroluj logy backendu.";
    default:
      return null;
  }
}
